import { getHeight, getWidth } from 'ol/extent';
import Polygon from 'ol/geom/Polygon';
import { getArea } from 'ol/sphere';

/**
 * A drawn search area checked before it is used, and why a plan of the next search failed. Plain
 * functions (EPSG:3857 rings), so they are tested without a map.
 */

// The backend takes a polygon of at most this many points; a longer drawing is simplified to fit
export const MAX_POLYGON_POINTS = 500;
// A smaller area holds no search: a slip of the mouse, not an area
export const MIN_AREA_M2 = 25;

/** A drawn area's size on the ground, square metres (geodesic: true metres, not Web Mercator's) */
export const polygonM2 = ring => getArea(new Polygon([ring]));

// Which side of the line p → q the point r lies: 1, -1, or 0 on the line
const orientation = (p, q, r) =>
	Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]));
// r, on the line p → q, lies between them
const within = (p, q, r) =>
	Math.min(p[0], q[0]) <= r[0] &&
	r[0] <= Math.max(p[0], q[0]) &&
	Math.min(p[1], q[1]) <= r[1] &&
	r[1] <= Math.max(p[1], q[1]);

/** The segments a → b and c → d meet: they cross, touch, or overlap. */
export const segmentsMeet = (a, b, c, d) => {
	const [o1, o2, o3, o4] = [
		orientation(a, b, c),
		orientation(a, b, d),
		orientation(c, d, a),
		orientation(c, d, b),
	];
	if (o1 !== o2 && o3 !== o4) return true;
	return (
		(o1 === 0 && within(a, b, c)) ||
		(o2 === 0 && within(a, b, d)) ||
		(o3 === 0 && within(c, d, a)) ||
		(o4 === 0 && within(c, d, b))
	);
};

// The ring's corners without repeats (a double-click to finish adds the last one twice), closed
const corners = ring => {
	const points = ring.filter(
		(point, k) => k === 0 || point[0] !== ring[k - 1][0] || point[1] !== ring[k - 1][1]
	);
	const [first] = points;
	const last = points[points.length - 1];
	if (first && (first[0] !== last[0] || first[1] !== last[1])) points.push(first);
	return points;
};

/**
 * A ring whose edges cross or touch, other than neighbours at their shared corner (a figure of eight).
 * Every pair of edges: O(n²), fine for MAX_POLYGON_POINTS.
 */
export const ringCrossesItself = ring => {
	const points = corners(ring);
	const edges = points.length - 1;
	for (let i = 0; i < edges; i += 1) {
		for (let j = i + 2; j < edges; j += 1) {
			// The first and last edges share the first corner
			if (i === 0 && j === edges - 1) continue;
			if (segmentsMeet(points[i], points[i + 1], points[j], points[j + 1])) return true;
		}
	}
	return false;
};

/**
 * A ring of at most `maxPoints` points: simplified with a tolerance that grows until it fits, from a
 * ten-thousandth of the ring's size. A ring that fits comes back as it is.
 */
export const simplifyRing = (ring, maxPoints = MAX_POLYGON_POINTS) => {
	if (ring.length <= maxPoints) return ring;
	const polygon = new Polygon([ring]);
	const extent = polygon.getExtent();
	let tolerance = Math.max(getWidth(extent), getHeight(extent)) / 10000;
	let simple = ring;
	while (simple.length > maxPoints) {
		simple = polygon.simplify(tolerance).getCoordinates()[0] ?? [];
		tolerance *= 1.5;
	}
	return simple;
};

/**
 * A drawn ring as the search takes it: `{ bounds, polygon }`, simplified to MAX_POLYGON_POINTS, or
 * `{ note }` saying why it cannot be searched (it crosses itself, or is too small).
 */
export const checkDrawnArea = ring => {
	const polygon = simplifyRing(ring);
	if (ringCrossesItself(polygon)) return { note: 'The area crosses itself; draw it again.' };
	if (polygon.length < 4 || polygonM2(polygon) < MIN_AREA_M2) {
		return { note: `The area is smaller than ${MIN_AREA_M2} m²; draw a larger one.` };
	}
	return { bounds: new Polygon([polygon]).getExtent(), polygon };
};

// What the backend says of an area it will not search, when an error comes without its status
const AREA_PROBLEM = /\b(tiles|squares)\b.*\blimit\b|outside|too (large|big|small)|polygon|area/i;

/**
 * A plan's error that is about the area (too large, outside the orthophoto, not a valid polygon: a
 * 422), so the search would fail too and Find waits for another area. Any other failure (compute,
 * the network) leaves Find to try: the plan is only a preview.
 */
export const planErrorBlocks = error => {
	if (error?.status) return error.status === 422;
	return AREA_PROBLEM.test(error?.message ?? '');
};
